import type Store from "@ember-data/store";
import { tracked } from "@glimmer/tracking";
import type { DmpAdapter } from "parog-web/cnam-adapters/dmp-adapter";
import type { InsiAdapter } from "parog-web/cnam-adapters/insi-adapter";
import type { FindDocumentsCriteria, INS } from "parog-web/cnam/types";
import {
  DmpAuthorizationStatus,
  DocumentStatus,
  DocumentVisibility,
  InsStatus
} from "parog-web/cnam/types";
import InsDocuments from "parog-web/components/cnam-services-access-form/ins-documents";
import RateExceeded from "parog-web/components/cnam-services-access-form/rate-exceeded";
import AuthorizationState from "parog-web/components/dmp-retrieve-access-form/authorization-state";
import SetSecretConnection from "parog-web/components/dmp-retrieve-access-form/set-secret-connection";
import type DmpDocumentMetadata from "parog-web/models/dmp-document-metadata";
import type Patient from "parog-web/models/patient";
import { CnamApiCpsNoPracticeLocations } from "parog-web/utils/cnam-api-errors";
import type { AuthenticationContext } from "../authentication-context";
import InsWithConfirmationRetrievalContext from "../ins-with-confirmation-retrieval-context";
import { State } from "./cnam-context";

export default abstract class DmpContext extends InsWithConfirmationRetrievalContext {
  @tracked hasCheckedPracticeLocations = false;
  @tracked hasCreatedConnector = false;
  @tracked declare insDocumentIsQualifying: boolean;
  @tracked declare insIdentityDocument?: string;
  @tracked declare isSecretConnection?: boolean;
  @tracked medicDmpAuthorizationStatus?: string;
  @tracked shouldAuthorizeMedicToAccessPatientDmp = false;

  declare authenticationContext: AuthenticationContext;
  declare dmpAdapter: DmpAdapter;
  declare insiAdapter: InsiAdapter;

  constructor(
    cnamAdapterName: string,
    patient: Patient,
    authenticationContext: AuthenticationContext,
    dmpAdapter: DmpAdapter,
    insiAdapter: InsiAdapter,
    store: Store,
    requestManager,
    onError?: () => void
  ) {
    super(
      cnamAdapterName,
      patient,
      authenticationContext,
      insiAdapter,
      dmpAdapter,
      store,
      requestManager,
      "dmp"
    );

    this.patient = patient;
    this.authenticationContext = authenticationContext;
    this.dmpAdapter = dmpAdapter;
    this.insiAdapter = insiAdapter;
    this.store = store;
    this.onError = onError;

    this.insIdentityDocument = patient.insIdentityDocument;
    this.insDocumentIsQualifying = patient.insDocumentIsQualifying;

    this.authenticationContext.forcedIdentifier =
      this.authenticationContext.medic.forceFinessForCnamRetrieval;

    this.isInsProcessNeeded = false;

    this.states = [
      ...this.states,
      new State(
        "check-practice-locations",
        async (): Promise<boolean> => {
          return Promise.resolve(!this.hasCheckedPracticeLocations);
        },
        async (): Promise<void> => {
          if (
            this.authenticationContext?.availableLocations?.length == 0 &&
            !this.authenticationContext?.medic.forceFinessForCnamRetrieval
          ) {
            throw new CnamApiCpsNoPracticeLocations();
          }
          this.hasCheckedPracticeLocations = true;
        }
      ),
      new State(
        "set-secret-connection",
        async (): Promise<boolean> => {
          return Promise.resolve(
            (await this.dmpAdapter.shouldSetSecretConnection(this.patient)) &&
              this.isSecretConnection == undefined
          );
        },
        async (): Promise<void> => {
          return;
        },
        SetSecretConnection
      ),
      new State(
        "ins-qualification-needed",
        async (): Promise<boolean> => {
          return Promise.resolve(
            this.patient.insStatus !== InsStatus.Qualified &&
              !this.isInsProcessNeeded
          );
        },
        async (): Promise<void> => {
          this.patient.insIdentityDocument = this.insIdentityDocument;
          this.patient.insDocumentIsQualifying = this.insDocumentIsQualifying!;
          await this.patient.save();
          this.isInsProcessNeeded = true;
        },
        InsDocuments
      ),
      new State(
        "rate-exceeded",
        (): Promise<boolean> => {
          return Promise.resolve(
            (this.dmpAdapter.hasExceededHourlyRate ||
              this.dmpAdapter.hasExceededDailyRate) &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          return;
        },
        RateExceeded
      ),
      new State(
        "create-connector",
        (): Promise<boolean> => {
          return Promise.resolve(
            !this.hasCreatedConnector &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          await this.dmpAdapter.createConnector();
          this.hasCreatedConnector = true;
          const medicDmpAuthorizationStatus = await this.dmpAdapter.td02Exist(
            this.patient.ins!,
            this.isSecretConnection!
          );
          this.shouldAuthorizeMedicToAccessPatientDmp =
            medicDmpAuthorizationStatus?.authorizationStatus !==
            DmpAuthorizationStatus.Authorized;
        }
      ),
      new State(
        "authorize-medic",
        (): Promise<boolean> => {
          return Promise.resolve(
            this.shouldAuthorizeMedicToAccessPatientDmp &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          await this.dmpAdapter.td03AddAuthorization(
            this.patient.ins!,
            this.isSecretConnection!
          );

          const medicDmpAuthorizationStatus = await this.dmpAdapter.td02Exist(
            this.patient.ins!,
            this.isSecretConnection!
          );
          this.shouldAuthorizeMedicToAccessPatientDmp =
            medicDmpAuthorizationStatus?.authorizationStatus !==
            DmpAuthorizationStatus.Authorized;
        },
        AuthorizationState
      )
    ];
  }

  async findDocuments(
    patient: Patient,
    store: Store,
    filterCriteria: FindDocumentsCriteria = {}
  ): Promise<Array<DmpDocumentMetadata>> {
    const criteria: FindDocumentsCriteria = Object.assign(
      {
        visibilities: [DocumentVisibility.NORMAL],
        statuses: [DocumentStatus.APPROVED]
      },
      filterCriteria
    );

    const documents = await this.dmpAdapter.td31Find(
      patient.ins as INS,
      criteria,
      this.isSecretConnection!
    );

    await store
      .createRecord("dmp-retrieval-started-journal-event", {
        cpsId: this.authenticationContext.cpsInformations!.medicInternalId,
        occurredAt: new Date(),
        patient,
        cnamProcessStartedJournalEvent: this.cnamProcessStartedJournalEvent
      })
      .save();

    return documents;
  }

  override reset() {
    super.reset();
    this.medicDmpAuthorizationStatus = undefined;
    this.shouldAuthorizeMedicToAccessPatientDmp = false;
    this.insIdentityDocument = this.patient.insIdentityDocument;
    this.insDocumentIsQualifying = this.patient.insDocumentIsQualifying;
    this.isSecretConnection = undefined;
    this.currentState = undefined;
    this.hasCreatedConnector = false;
    this.hasCheckedPracticeLocations = false;
  }
}
